import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Box,
  Stack,
  IconButton,
  Typography,
  Divider,
  Tooltip,
} from '@mui/material';
import { Delete, Link as LinkIcon, Add } from '@mui/icons-material';
import { DataGrid, GridColDef, deDE } from '@mui/x-data-grid';

import type { ILegalClient } from '../Interfaces/ILegalClient';
import type { ILegalClientGroup } from '../Interfaces/ILegalClientGroup';
import { CustomeTextField } from '../generic/CustomeTextField';

import {
  useGetLegalClientGroupMembers,
  useSetLegalClientGroupMembers,
} from '../hooks/reactQuerys/LegalClientGroups';

import { AssignLegalClientsDialog } from '../LegalTransaction/LegalClient/AssignLegalClientsDialog';

interface Props {
  open: boolean;
  group: ILegalClientGroup;
  isSaving: boolean;
  onChange: (g: ILegalClientGroup) => void;
  onClose: () => void;

  onSave: () => Promise<ILegalClientGroup>;

  onDeleteRequest: () => void;
}

export const LegalClientGroupEdit = ({
  open,
  group,
  isSaving,
  onChange,
  onClose,
  onSave,
  onDeleteRequest,
}: Props) => {
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [draftMembers, setDraftMembers] = useState<ILegalClient[]>([]);

  const isEdit = group.idLegalClientGroup > 0;

  const { members, isLoadingMembers, refetchMembers } =
    useGetLegalClientGroupMembers(isEdit ? group.idLegalClientGroup : null);

  const { setMembers, isPendingSetMembers } = useSetLegalClientGroupMembers();

  const shownMembers = isEdit ? members : draftMembers;

  const isBusy = isSaving || isLoadingMembers || isPendingSetMembers;

  const handleAssign = async (newLegalClients: ILegalClient[]) => {
    if (isEdit) {
      await setMembers({
        idLegalClientGroup: group.idLegalClientGroup,
        ids: newLegalClients.map(lc => lc.idLegalClient),
      });
      await refetchMembers();
    } else {
      setDraftMembers(newLegalClients);
    }
  };

  const handleRemoveMember = async (idLegalClient: number) => {
    const next = shownMembers.filter(m => m.idLegalClient !== idLegalClient);

    if (isEdit) {
      await setMembers({
        idLegalClientGroup: group.idLegalClientGroup,
        ids: next.map(m => m.idLegalClient),
      });
      await refetchMembers();
    } else {
      setDraftMembers(next);
    }
  };

  const memberColumns: GridColDef<ILegalClient>[] = useMemo(
    () => [
      { field: 'idLegalClient', headerName: 'Nr.', width: 90 },
      {
        field: 'Email',
        headerName: 'E-Mail-Adresse',
        flex: 1,
        minWidth: 180,
        valueGetter: params => params.row.Email ?? '',
        renderCell: params => (params.row.Email ? params.row.Email : '-'),
        sortable: false,
      },
      {
        field: 'FirstName',
        headerName: 'Vorname',
        flex: 1,
        minWidth: 140,
        valueGetter: params => params.row.FirstName ?? '',
        renderCell: params =>
          params.row.FirstName ? params.row.FirstName : '-',
      },
      {
        field: 'LastName',
        headerName: 'Nachname/Firma',
        flex: 1.2,
        minWidth: 180,
        valueGetter: params => params.row.LastName ?? '',
        renderCell: params => (params.row.LastName ? params.row.LastName : '-'),
      },
      {
        field: 'actions',
        headerName: '',
        width: 110,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: params => (
          <Box display='flex' alignItems='center' gap={0.5}>
            <Tooltip title='Mandant anzeigen'>
              <IconButton
                size='small'
                onClick={e => {
                  e.stopPropagation();
                  window.open(
                    `/clients/${params.row.idLegalClient}`,
                    '_blank',
                    'noopener,noreferrer'
                  );
                }}
              >
                <LinkIcon fontSize='small' />
              </IconButton>
            </Tooltip>

            <Tooltip title='Mandant aus Gruppe entfernen'>
              <IconButton
                size='small'
                onClick={async e => {
                  e.stopPropagation();
                  await handleRemoveMember(params.row.idLegalClient);
                }}
                disabled={isBusy}
              >
                <Delete fontSize='small' />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [isBusy, shownMembers]
  );

  const title = isEdit
    ? 'Mandantengruppe bearbeiten'
    : 'Mandantengruppe erstellen';

  const handleSaveWithMembers = async () => {
    const savedGroup = await onSave();

    if (savedGroup.idLegalClientGroup > 0 && draftMembers.length > 0) {
      await setMembers({
        idLegalClientGroup: savedGroup.idLegalClientGroup,
        ids: draftMembers.map(lc => lc.idLegalClient),
      });
      setDraftMembers([]);
      await refetchMembers();
    }

    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
        <DialogTitle>
          {title}
          {isEdit && (
            <IconButton
              onClick={onDeleteRequest}
              sx={{ float: 'right', mt: -0.5 }}
            >
              <Delete />
            </IconButton>
          )}
        </DialogTitle>

        <DialogContent>
          {isBusy && <LinearProgress sx={{ mb: 2 }} />}

          <Stack spacing={2} sx={{ mt: 2 }}>
            {/* 1. Zeile: Name + Farbe + HEX */}
            <Box display='flex' alignItems='center' gap={2}>
              <Box sx={{ flexGrow: 1 }}>
                <CustomeTextField
                  label='Name'
                  type='string'
                  attr='Name'
                  object={group}
                  setObject={onChange}
                  required
                  size='small'
                />
              </Box>

              <CustomeTextField
                label='Farbe'
                type='color'
                attr='ColorHex'
                object={group}
                setObject={onChange}
                size='small'
                maxWidth={150}
              />
            </Box>

            {/* Beschreibung */}
            <CustomeTextField
              label='Beschreibung'
              type='string'
              attr='Description'
              object={group}
              setObject={onChange}
              rows={3}
              size='small'
              null
            />

            {/* Mitglieder */}
            <>
              <Divider sx={{ my: 1 }} />

              <Box
                display='flex'
                alignItems='center'
                justifyContent='space-between'
                gap={2}
              >
                <Typography variant='subtitle1'>
                  Mitglieder ({shownMembers.length})
                </Typography>

                <Button
                  size='small'
                  startIcon={<Add />}
                  onClick={() => setIsAssignOpen(true)}
                  disabled={isBusy}
                >
                  Mandanten hinzufügen
                </Button>
              </Box>

              <Box sx={{ width: '100%' }}>
                <DataGrid
                  localeText={
                    deDE.components.MuiDataGrid.defaultProps.localeText
                  }
                  rows={shownMembers}
                  columns={memberColumns}
                  getRowId={row => row.idLegalClient}
                  autoHeight
                  density='compact'
                  loading={isEdit ? isLoadingMembers : false}
                  disableRowSelectionOnClick
                  onRowClick={params => {
                    window.open(
                      `/clients/${params.row.idLegalClient}`,
                      '_blank',
                      'noopener,noreferrer'
                    );
                  }}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10, page: 0 } },
                  }}
                  pageSizeOptions={[5, 10, 25]}
                />
              </Box>
            </>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant='outlined' onClick={onClose} disabled={isBusy}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            onClick={handleSaveWithMembers}
            disabled={!group.Name.trim() || isBusy}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {isAssignOpen && (
        <AssignLegalClientsDialog
          assignedLegalClients={shownMembers}
          setAssignedLegalClients={async (newLegalClients: ILegalClient[]) => {
            await handleAssign(newLegalClients);
            setIsAssignOpen(false);
          }}
          unmount={() => setIsAssignOpen(false)}
        />
      )}
    </>
  );
};
